import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  createLifeCatchUp,
  fetchLifeCatchUpPeople,
  fetchLifeCatchUps,
  fetchScopedCentres
} from 'api'
import {
  ListPagination,
  usePagedRows
} from 'app/components/generic/ListPagination'
import {
  SearchHeadCell,
  useColumnSearch
} from 'app/components/generic/SearchHeadCell'
import constants from 'app/constants'
import { showToast } from 'app/helpers'
import { useOpsCentre } from 'app/hooks/useOpsCentre'

const extractRows = (data) =>
  (data && (data.results || data.data)) || (Array.isArray(data) ? data : [])

const todayISO = () => new Date().toISOString().slice(0, 10)

const ALL_CENTRES = 'all'

const personName = (p) => (p && p.name) || '—'
const centreName = (r) => (r.centre && r.centre.name) || '—'

// What each table column shows, for the search boxes in its heading.
const CATCH_UP_SEARCH = {
  date: (r) => r.date,
  centre: centreName,
  manager: (r) => personName(r.manager),
  member: (r) => personName(r.team_member),
  review: (r) => r.review_date
}

const blankForm = (centre = '') => ({
  date: todayISO(),
  centre,
  manager: null, // selected {uuid, name} option
  teamMember: null,
  continueDoing: '',
  areaOfImprovement: '',
  agreedNextSteps: '',
  concerns: '',
  supportNeeded: '',
  reviewDate: ''
})

// The five free-text fields, declared once so the dialog and the detail view
// stay in lock-step with the catch-up sheet's columns.
const TEXT_FIELDS = [
  { key: 'continueDoing', label: 'Continue Doing' },
  { key: 'areaOfImprovement', label: 'Area of Improvement' },
  { key: 'agreedNextSteps', label: 'Agreed Next Steps' },
  { key: 'concerns', label: 'Any Concerns?' },
  { key: 'supportNeeded', label: 'Support Needed' }
]

/**
 * Async, type-to-search picker for people. `role` is 'manager' (admins) or
 * 'member' (admins + teachers). Debounced so we don't hit the API on every
 * keystroke.
 */
const PeoplePicker = ({ role, label, value, onChange }) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const timer = useRef(null)

  const search = useCallback(
    (text) => {
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(async () => {
        setLoading(true)
        try {
          const data = await fetchLifeCatchUpPeople({ role, search: text })
          setOptions(Array.isArray(data) ? data : extractRows(data))
        } catch (error) {
          // surfaced once on save; keep the picker quiet on transient errors
        } finally {
          setLoading(false)
        }
      }, 300)
    },
    [role]
  )

  // Prime with an initial (unfiltered) list when first opened.
  useEffect(() => {
    search('')
    return () => timer.current && clearTimeout(timer.current)
  }, [search])

  return (
    <Autocomplete
      options={options}
      value={value}
      loading={loading}
      getOptionLabel={(o) => (o && o.name) || ''}
      getOptionSelected={(o, v) => o.uuid === (v && v.uuid)}
      onChange={(e, selected) => onChange(selected)}
      onInputChange={(e, text, reason) => {
        if (reason === 'input') search(text)
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  )
}

const LifeCatchUps = () => {
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState(null) // null = create dialog closed
  const [viewing, setViewing] = useState(null) // null = detail dialog closed
  const [saving, setSaving] = useState(false)
  // Only the centres this person is mapped to, as on the Newsletter page
  // (Satya, 23 Oct); HQ gets every centre.
  const [centres, setCentres] = useState([])
  const [unrestricted, setUnrestricted] = useState(false)
  // Shared by every Centre Operations page and kept across refreshes.
  const [listCentre, setListCentre, pickListCentre] = useOpsCentre({
    fallback: ALL_CENTRES
  })

  // The API pages 25 at a time; follow `next` so the table holds every
  // catch-up and its own paging and search cover them all.
  const loadRecords = useCallback(async () => {
    setLoading(true)
    try {
      let all = []
      let data = await fetchLifeCatchUps({ queries: { limit: 100 } })
      all = all.concat(extractRows(data))
      while (data && data.next) {
        data = await fetchLifeCatchUps({ url: data.next })
        all = all.concat(extractRows(data))
      }
      setRecords(all)
    } catch (error) {
      showToast('Could not load Life Catch-Ups', constants.ERROR)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRecords()
  }, [loadRecords])

  useEffect(() => {
    let active = true
    fetchScopedCentres()
      .then((data) => {
        if (!active) return
        const list = (data && (data.results || data.centres)) || []
        setCentres(list)
        setUnrestricted(Boolean(data && data.unrestricted))
        // A saved centre this person is not mapped to shows every centre.
        setListCentre((prev) =>
          prev === ALL_CENTRES || list.some((c) => c.uuid === prev)
            ? prev
            : ALL_CENTRES
        )
      })
      .catch(() => showToast('Could not load centres', constants.ERROR))
    return () => {
      active = false
    }
  }, [])

  const inCentre = useMemo(
    () =>
      listCentre === ALL_CENTRES
        ? records
        : records.filter((r) => r.centre && r.centre.uuid === listCentre),
    [records, listCentre]
  )
  const colSearch = useColumnSearch(inCentre, CATCH_UP_SEARCH)
  const { pageRows, paginationProps, resetPage } = usePagedRows(colSearch.rows)
  useEffect(() => {
    resetPage()
  }, [listCentre, colSearch.search])

  // A new catch-up starts on the centre picked above, or the only one.
  const openAdd = () =>
    setForm(
      blankForm(
        listCentre !== ALL_CENTRES
          ? listCentre
          : centres.length === 1
          ? centres[0].uuid
          : ''
      )
    )

  // Read e.target.value synchronously, then update. React 16 pools/nullifies
  // synthetic events after the handler returns, so referencing e.target inside
  // the deferred setForm updater would crash ("Cannot read 'value' of null").
  const updateField = (key) => (e) => {
    const { value } = e.target
    setForm((f) => ({ ...f, [key]: value }))
  }

  const save = async () => {
    if (!form.date || !form.centre || !form.manager || !form.teamMember) {
      showToast(
        'Date, Centre, Manager and Team Member are required',
        constants.ERROR
      )
      return
    }
    const payload = {
      date: form.date,
      centre: form.centre,
      manager: form.manager.uuid,
      team_member: form.teamMember.uuid,
      continue_doing: form.continueDoing,
      area_of_improvement: form.areaOfImprovement,
      agreed_next_steps: form.agreedNextSteps,
      concerns: form.concerns,
      support_needed: form.supportNeeded,
      review_date: form.reviewDate || null
    }
    setSaving(true)
    try {
      await createLifeCatchUp(payload)
      setForm(null)
      showToast('Catch-up saved', constants.SUCCESS)
      loadRecords()
    } catch (error) {
      const detail = error && error.response && error.response.data
      showToast(
        (detail && (detail.detail || JSON.stringify(detail))) ||
          'Could not save catch-up',
        constants.ERROR
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box p={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h5">Life Catch-Ups</Typography>
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 220 }}
          >
            <InputLabel>Centre</InputLabel>
            <Select
              label="Centre"
              value={listCentre}
              onChange={(e) => pickListCentre(e.target.value)}
            >
              <MenuItem value={ALL_CENTRES}>
                {unrestricted ? 'All centres' : 'All my centres'}
              </MenuItem>
              {centres.map((c) => (
                <MenuItem key={c.uuid} value={c.uuid}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" color="primary" onClick={openAdd}>
            + New Catch-Up
          </Button>
        </Box>
      </Box>

      {/* No scroll box around the table: the heading sticks to the top of the
          window, and the table fits the window width. */}
      <Table size="small">
        <TableHead>
          <TableRow>
            <SearchHeadCell label="Date" search={colSearch.box('date')} />
            <SearchHeadCell label="Centre" search={colSearch.box('centre')} />
            <SearchHeadCell label="Manager" search={colSearch.box('manager')} />
            <SearchHeadCell
              label="Team Member"
              search={colSearch.box('member')}
            />
            <SearchHeadCell
              label="Review Date"
              search={colSearch.box('review')}
            />
            <SearchHeadCell label="Details" align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <CircularProgress size={22} />
              </TableCell>
            </TableRow>
          )}
          {!loading && !colSearch.rows.length && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                {records.length
                  ? 'No catch-ups match.'
                  : 'No catch-ups recorded yet.'}
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            pageRows.map((r) => (
              <TableRow key={r.uuid} hover>
                <TableCell style={{ whiteSpace: 'nowrap' }}>{r.date}</TableCell>
                <TableCell>{centreName(r)}</TableCell>
                <TableCell>{personName(r.manager)}</TableCell>
                <TableCell>{personName(r.team_member)}</TableCell>
                <TableCell>{r.review_date || '—'}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => setViewing(r)}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <ListPagination {...paginationProps} />

      {/* Create dialog */}
      <Dialog
        open={!!form}
        onClose={() => setForm(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>New Life Catch-Up</DialogTitle>
        <DialogContent>
          {form && (
            <Box
              display="flex"
              flexDirection="column"
              style={{ gap: 12 }}
              pt={1}
            >
              <TextField
                type="date"
                label="Date"
                required
                value={form.date}
                onChange={updateField('date')}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl required>
                <InputLabel shrink>Centre</InputLabel>
                <Select
                  value={form.centre}
                  displayEmpty
                  onChange={updateField('centre')}
                >
                  <MenuItem value="" disabled>
                    Pick a centre
                  </MenuItem>
                  {centres.map((c) => (
                    <MenuItem key={c.uuid} value={c.uuid}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <PeoplePicker
                role="manager"
                label="Manager"
                value={form.manager}
                onChange={(v) => setForm((f) => ({ ...f, manager: v }))}
              />
              <PeoplePicker
                role="member"
                label="Team Member"
                value={form.teamMember}
                onChange={(v) => setForm((f) => ({ ...f, teamMember: v }))}
              />
              {TEXT_FIELDS.map(({ key, label }) => (
                <TextField
                  key={key}
                  label={label}
                  multiline
                  minRows={2}
                  value={form[key]}
                  onChange={updateField(key)}
                />
              ))}
              <TextField
                type="date"
                label="Review Date"
                value={form.reviewDate}
                onChange={updateField('reviewDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={save}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail (read-only) dialog */}
      <Dialog
        open={!!viewing}
        onClose={() => setViewing(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Life Catch-Up</DialogTitle>
        <DialogContent>
          {viewing && (
            <Box
              display="flex"
              flexDirection="column"
              style={{ gap: 10 }}
              pt={1}
            >
              <Typography variant="body2">
                <strong>Date:</strong> {viewing.date}
              </Typography>
              <Typography variant="body2">
                <strong>Centre:</strong> {centreName(viewing)}
              </Typography>
              <Typography variant="body2">
                <strong>Manager:</strong> {personName(viewing.manager)}
              </Typography>
              <Typography variant="body2">
                <strong>Team Member:</strong> {personName(viewing.team_member)}
              </Typography>
              {TEXT_FIELDS.map(({ key, label }) => {
                // map camelCase UI key -> snake_case API field
                const apiKey = key.replace(
                  /[A-Z]/g,
                  (m) => `_${m.toLowerCase()}`
                )
                return (
                  <Typography key={key} variant="body2">
                    <strong>{label}:</strong> {viewing[apiKey] || '—'}
                  </Typography>
                )
              })}
              <Typography variant="body2">
                <strong>Review Date:</strong> {viewing.review_date || '—'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewing(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default LifeCatchUps
